import React, { useState } from 'react';
import { Button, Checkbox, Input, Tooltip } from 'antd';
import SystemModal from 'components/Shared/Modal';
import Can from 'config/Can';
import useTranslation from 'utils/hooks/useTranslation';

import styles from './style.module.scss';

const FundingRequestReviewModal = ({
  visible,
  onClose,
  fundingRequest,
  onApprove,
  onDecline,
  isApproving,
  isDeclining,
}) => {
  const { translateText } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');

  const resetAndClose = () => {
    setConfirmed(false);
    setNote('');
    onClose();
  };

  if (!fundingRequest) {
    return null;
  }

  const {
    id,
    account,
    requester,
    date,
    amount,
    transferReference,
    proofOfPayment,
  } = fundingRequest;

  const handleApprove = () => {
    onApprove(id, note);
  };

  const handleDecline = () => {
    onDecline(id, note);
  };

  return (
    <SystemModal
      title={
        <>
          <div className={styles.reviewModalTitle}>
            {translateText('corporateAccounts.fundingRequests.reviewModal.title')}
          </div>
          <div className={styles.reviewModalSubtitle}>
            {translateText('corporateAccounts.fundingRequests.reviewModal.subtitle', {
              id,
              date,
            })}
          </div>
        </>
      }
      titleLevel={5}
      width={480}
      visible={visible}
      onCancel={resetAndClose}
      footer={null}
      destroyOnClose
      centered
      data-testid="fundingRequestReviewModal"
    >
      <div className={styles.reviewModalRow}>
        <span className={styles.reviewModalLabel}>
          {translateText('corporateAccounts.fundingRequests.table.account')}
        </span>
        <span className={`${styles.accountName} ${styles.reviewModalValue}`}>{account}</span>
      </div>
      <div className={styles.reviewModalRow}>
        <span className={styles.reviewModalLabel}>
          {translateText('corporateAccounts.fundingRequests.table.requestedBy')}
        </span>
        <span className={styles.reviewModalValue}>{requester}</span>
      </div>
      <div className={styles.reviewModalRow}>
        <span className={styles.reviewModalLabel}>
          {translateText('corporateAccounts.fundingRequests.table.amount')}
        </span>
        <span className={styles.reviewModalValue}>{amount}</span>
      </div>
      <div className={styles.reviewModalRow}>
        <span className={styles.reviewModalLabel}>
          {translateText('corporateAccounts.fundingRequests.reviewModal.transferReference')}
        </span>
        <span className={styles.reviewModalValue}>{transferReference}</span>
      </div>

      <div className={styles.reviewModalProof} data-testid="fundingRequestReviewModalProof">
        <div className={styles.reviewModalProofDetails}>
          <div className={styles.reviewModalProofName}>{proofOfPayment}</div>
          <div className={styles.reviewModalProofCaption}>
            {translateText('corporateAccounts.fundingRequests.reviewModal.proofOfPaymentCaption')}
          </div>
        </div>
        <span className={styles.proofLink}>
          {translateText('corporateAccounts.fundingRequests.reviewModal.view')}
        </span>
      </div>

      <Checkbox
        checked={confirmed}
        onChange={event => setConfirmed(event.target.checked)}
        className={styles.reviewModalConfirmCheckbox}
        data-testid="fundingRequestReviewModalConfirm"
      >
        {translateText('corporateAccounts.fundingRequests.reviewModal.confirmCheckbox')}
      </Checkbox>

      <div className={styles.reviewModalNoteLabel}>
        {translateText('corporateAccounts.fundingRequests.reviewModal.noteLabel')}
      </div>
      <div className={styles.reviewModalNoteCaption}>
        {translateText('corporateAccounts.fundingRequests.reviewModal.noteCaption')}
      </div>
      <Input.TextArea
        value={note}
        onChange={event => setNote(event.target.value)}
        placeholder={translateText('corporateAccounts.fundingRequests.reviewModal.notePlaceholder')}
        rows={3}
        data-testid="fundingRequestReviewModalNote"
      />

      <div className={styles.reviewModalFooter}>
        <Can I="decline" a="FundingRequest" passThrough>
          {allowed => {
            const isDisabled = !allowed || isDeclining || confirmed || !note.trim();
            const disabledReason = !allowed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.declineDisabledPermission',
                )
              : confirmed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.declineDisabledConfirmed',
                )
              : !note.trim()
              ? translateText('corporateAccounts.fundingRequests.reviewModal.declineDisabledNote')
              : null;

            return (
              <Tooltip title={isDisabled ? disabledReason : null}>
                <span className={styles.reviewModalButtonWrapper}>
                  <Button
                    danger
                    ghost
                    block
                    disabled={isDisabled}
                    loading={isDeclining}
                    onClick={handleDecline}
                    className={styles.reviewModalDeclineButton}
                    data-testid="fundingRequestReviewModalDecline"
                  >
                    {translateText('corporateAccounts.fundingRequests.reviewModal.decline')}
                  </Button>
                </span>
              </Tooltip>
            );
          }}
        </Can>
        <Can I="approve" a="FundingRequest" passThrough>
          {allowed => {
            const isDisabled = !allowed || !confirmed || isApproving;
            const disabledReason = !allowed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.approveDisabledPermission',
                )
              : !confirmed
              ? translateText(
                  'corporateAccounts.fundingRequests.reviewModal.approveDisabledConfirm',
                )
              : null;

            return (
              <Tooltip title={isDisabled ? disabledReason : null}>
                <span
                  className={`${styles.reviewModalButtonWrapper} ${styles.reviewModalApproveWrapper}`}
                >
                  <Button
                    type="primary"
                    block
                    disabled={isDisabled}
                    loading={isApproving}
                    onClick={handleApprove}
                    className={styles.reviewModalApproveButton}
                    data-testid="fundingRequestReviewModalApprove"
                  >
                    {translateText('corporateAccounts.fundingRequests.reviewModal.approve')}
                  </Button>
                </span>
              </Tooltip>
            );
          }}
        </Can>
      </div>
    </SystemModal>
  );
};

export default FundingRequestReviewModal;
